import React from 'react';
import { History, Calendar, RefreshCw, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatCurrency } from '../../lib/utils';
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';

const ANALYSIS_TYPES = {
  single: 'Single Measure',
  portfolio: 'Portfolio',
  matrix: 'Full Matrix'
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DifferenceIndicator = ({ diff, inverse = false }) => {
  if (diff === null || diff === undefined || isNaN(diff)) return <Minus className="h-4 w-4 text-slate-400" />;
  const isPositive = inverse ? diff < 0 : diff > 0;
  const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
  const color = isPositive ? 'text-emerald-600' : 'text-rose-600';
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(diff).toFixed(1)}%
    </span>
  );
};

export const TrendAnalysisTab = ({ scenarioGroups, selectedGroup, handleSelectGroup, currency, navigate }) => {
  const trendData = selectedGroup?.scenarios?.map(s => ({
    date: formatDate(s.created_at || s.run_date),
    version: `v${s.version || 1}`,
    ROI: (s.roi_percent || 0) / 100,
    'Financial Impact': (s.financial_impact || 0) / 1000,
    'KPI Uplift': s.kpi_delta_percent || 0,
  })) || [];

  return (
    <div className="space-y-6">
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-start gap-2">
            <History className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Track ROI Realization Over Time</p>
              <p className="text-blue-700">
                Select a scenario group to see how metrics have changed across multiple runs.
                This helps track actual ROI realization as training data is collected over time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scenario Group Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-teal-600" />
            Select Scenario to Track
          </CardTitle>
          <CardDescription>Choose a scenario with multiple runs to view trends</CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedGroup?.group_id || ''}
            onValueChange={handleSelectGroup}
          >
            <SelectTrigger className="max-w-md" data-testid="trend-group-select">
              <SelectValue placeholder="Select a scenario..." />
            </SelectTrigger>
            <SelectContent>
              {scenarioGroups.map((g) => (
                <SelectItem key={g.group_id} value={g.group_id}>
                  <div className="flex items-center gap-2">
                    <span>{g.name}</span>
                    <Badge variant="secondary" className="text-[10px]">
                      {ANALYSIS_TYPES[g.analysis_type] || 'Single Measure'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {g.version_count} run{g.version_count > 1 ? 's' : ''}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {scenarioGroups.length === 0 && (
            <p className="text-sm text-slate-500 mt-4">
              No scenario groups found. Save multiple runs of the same scenario to track trends.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Trend Charts */}
      {selectedGroup && selectedGroup.scenarios?.length > 1 ? (
        <>
          {/* Trend Summary */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{selectedGroup.name} - Trend Summary</CardTitle>
                  <CardDescription>
                    {selectedGroup.version_count} runs from {formatDate(selectedGroup.earliest_date)} to {formatDate(selectedGroup.latest_date)}
                  </CardDescription>
                </div>
                <Badge className="bg-navy-100 text-navy-700 hover:bg-navy-100">
                  {ANALYSIS_TYPES[selectedGroup.analysis_type] || 'Single Measure'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-600 font-medium">Analysis Type</p>
                  <p className="text-lg font-bold text-slate-700">{ANALYSIS_TYPES[selectedGroup.analysis_type] || 'Single'}</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4">
                  <p className="text-xs text-teal-600 font-medium">Total Runs</p>
                  <p className="text-2xl font-bold text-teal-700">{selectedGroup.version_count}</p>
                </div>
                <div className="bg-navy-50 rounded-lg p-4">
                  <p className="text-xs text-navy-600 font-medium">Latest ROI</p>
                  <p className="text-2xl font-bold text-navy-700">
                    {((selectedGroup.scenarios[selectedGroup.scenarios.length - 1]?.roi_percent || 0) / 100).toFixed(1)}x
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-xs text-purple-600 font-medium">First Run ROI</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {((selectedGroup.scenarios[0]?.roi_percent || 0) / 100).toFixed(1)}x
                  </p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-xs text-emerald-600 font-medium">ROI Change</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {(() => {
                      const first = selectedGroup.scenarios[0]?.roi_percent || 0;
                      const last = selectedGroup.scenarios[selectedGroup.scenarios.length - 1]?.roi_percent || 0;
                      const change = first > 0 ? ((last - first) / first * 100) : 0;
                      return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
                    })()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trend Line Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Metrics Over Time</CardTitle>
              <CardDescription>Track how key metrics evolve across runs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="ROI" stroke="#0d9488" strokeWidth={2} dot={{ fill: '#0d9488', r: 4 }} name="ROI (x)" />
                    <Line type="monotone" dataKey="KPI Uplift" stroke="#1e3a5f" strokeWidth={2} dot={{ fill: '#1e3a5f', r: 4 }} name="KPI Uplift (%)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Version History Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Run History</CardTitle>
              <CardDescription>All recorded runs for this scenario</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Version</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Date</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">ROI</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Financial Impact</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">KPI Uplift</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedGroup.scenarios.map((s, idx) => {
                      const prevRoi = idx > 0 ? selectedGroup.scenarios[idx - 1].roi_percent : null;
                      const change = prevRoi ? ((s.roi_percent - prevRoi) / prevRoi * 100) : null;
                      return (
                        <tr key={s.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-4"><Badge variant="outline">v{s.version || idx + 1}</Badge></td>
                          <td className="py-3 px-4">{formatDate(s.created_at)}</td>
                          <td className="text-right py-3 px-4 font-semibold">{((s.roi_percent || 0) / 100).toFixed(1)}x</td>
                          <td className="text-right py-3 px-4">{formatCurrency(Math.round(s.financial_impact || 0, currency))}</td>
                          <td className="text-right py-3 px-4">{(s.kpi_delta_percent || 0).toFixed(2)}%</td>
                          <td className="text-right py-3 px-4">
                            {change !== null ? <DifferenceIndicator diff={change} /> : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : selectedGroup ? (
        <Card className="border-dashed">
          <CardContent className="py-12">
            <div className="text-center">
              <RefreshCw className="h-10 w-10 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">Only One Run Available</h3>
              <p className="text-slate-500 mt-2 max-w-md mx-auto">
                This scenario only has one run. Re-run the scenario with updated data to start tracking trends.
              </p>
              <Button onClick={() => navigate('/scenarios')} variant="outline" className="mt-4">
                Go to Scenarios
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-teal-200 bg-teal-50/30">
          <CardContent className="py-8">
            <div className="text-center">
              <p className="text-slate-600">Select a scenario above to view its trend over time</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
